import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../AuthContext';
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';
import CheckOutlineIcon from '@rsuite/icons/CheckOutline';
import { Table, Button, InputGroup, Input } from 'rsuite';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Loader } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import RemindOutlineIcon from '@rsuite/icons/RemindOutline';

const { Column, HeaderCell, Cell } = Table;

function DelticomOrder() {
  const { auth } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [delticom, setDelticom] = useState([]);
  const [parcelNrs, setParcelNrs] = useState([]);
  const [order, setOrder] = useState(null);
  const [contact, setContact] = useState(null);
  const navigate = useNavigate(); // Initialize useNavigate
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id"); // Extract 'id' from URL
  const [quantity, setQuantity] = useState(2);

  const fetchData = async () => {
    setLoading(true);

    try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/delticom/order-info?id=${orderId}`, { // Removed extra '}'
            method: "GET",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.token}`
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Misslyckades att hämta orderdata: ${errorText}`);
        }

        const data = await response.json();

        // ✅ Update state variables
        setOrder(data.order);
        setContact(data.customer);
        setParcelNrs(data.parcelNumbers);
        setQuantity(parseInt(data.order.qty));
        setDelticom(data.delticom)

    } catch (error) {
        console.error("Error fetching order info:", error.message);
        toast.error(error.message, {
            position: 'bottom-right'
        });
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
        fetchData();
    }
  }, [orderId]);

  const generateDelivery = async () => {
  
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/delticom/generate-delivery?id=${delticom.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDelticom(data);
        toast.info('Fraktsedel genererad.', {
            position: 'bottom-right'
          });
      } else {
        const errorText = await response.text();
        throw new Error(errorText);
      }
    } catch (error) {
      console.error('Misslyckades att markera som hanterad: ', error);
      toast.error('Misslyckades att markera som hanterad.', {
        position: 'bottom-right'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCreditInvoice = async (quantity) => {

    if(quantity>order?.qty){
        toast.error('Antal att kreditera kan inte vara mer än det som beställts.', {
            position: 'bottom-right'
          });
        return;
    }

    if(quantity<1){
        toast.error('Antal måste vara över 0.', {
            position: 'bottom-right'
          });
        return;
    }
  
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/delticom/generate-credit-invoice?id=${delticom.id}&qty=${quantity}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDelticom(data);
        toast.info(`Kreditfaktura genererad.`, {
            position: 'bottom-right'
          });
      } else {
        const errorText = await response.text();
        throw new Error(errorText);
      }
    } catch (error) {
      console.error('Misslyckades att generera kreditfaktura: ', error);
      toast.error('Misslyckades att generera kreditfaktura.', {
        position: 'bottom-right'
      });
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <>
        <Helmet>
          <title>{delticom?.orderNr}</title>
        </Helmet>
        <div className='px-5 pt-4 w-100'>
        <h4 className="mb-2">{delticom?.orderNr}</h4>
        <p className="text-muted">Order skapad {delticom?.dateCreated}</p>
        <hr />

        <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-8">
                <div className="d-flex mb-2">
                    {delticom?.statusCode !== 201 && (
                        <Button
                        className="me-2"
                        appearance="default"
                        disabled={loading}
                        onClick={generateDelivery}
                      >
                        {loading ? <Loader /> : "Generera fraktsedel"}
                      </Button>
                      
                    )}
                    {delticom?.parcelUrl && (
                        <Button
                            className="me-2"
                            appearance="default"
                            onClick={() => window.open(delticom?.parcelUrl, '_blank')}
                        >
                            Ladda ner fraktsedel
                        </Button>
                    )}

                    {delticom.filled && (
                        <div className="ms-auto">
                        <InputGroup>
                            <Input
                            size="md"
                            value={quantity}
                            onChange={(value, e) => setQuantity(Number(value))}
                            style={{ width: "50px" }}
                            type="number"
                            min="1"
                            disabled={delticom?.invoiceGeneratedNegative}  // Disable input if condition is true
                            />
                            <InputGroup.Button
                            onClick={() => generateCreditInvoice(quantity)}
                            disabled={delticom?.invoiceGeneratedNegative}  // Disable button if condition is true
                            >
                            {delticom?.invoiceGeneratedNegative ? 'Kreditfaktura genererad' : 'Generera kreditfaktura'}
                            </InputGroup.Button>
    
                        </InputGroup>
                        </div>
                    )}

                </div>

                <Table
                className="rounded"
                autoHeight
                cellBordered
                bordered
                data={[{
                    prodNo: delticom?.prodNo,
                    description: order?.productName,
                    quantity: order?.qty,
                    price: order?.price
                }]}  // Always use the 'order' object as the single row
                autoWidth
                >
                <Column width={150}>
                    <HeaderCell>Produktnummer</HeaderCell>
                    <Cell dataKey="prodNo" />
                </Column>

                <Column flexGrow={1}>
                    <HeaderCell>Produktnamn</HeaderCell>
                    <Cell dataKey="description" />
                </Column>

                <Column width={80} align="center">
                    <HeaderCell>Antal</HeaderCell>
                    <Cell dataKey="quantity" />
                </Column>

                <Column width={80} align="center">
                    <HeaderCell>Pris</HeaderCell>
                    <Cell dataKey="price" />
                </Column>
                </Table>

                <div className="row mt-2">
                    <div className="col-sm-12 col-md-12 col-lg-4">
                        <div className="card shadow p-2">
                            <div className="d-flex align-items-center">
                                <InfoOutlineIcon className='me-2' />
                                <p className='mb-0'>Ex. moms</p>
                            </div>
                            <h3 className='mb-0'>
                            {order?.total}
                            </h3>
                        </div>
                    </div>
                    <div className="col-sm-12 col-md-12 col-lg-4">
                        <div className="card shadow p-2">
                            <div className="d-flex align-items-center">
                                <InfoOutlineIcon className='me-2' />
                                <p className='mb-0'>Ink. moms</p>
                            </div>
                            <h3 className='mb-0'>
                                {delticom?.totalWithVat}
                            </h3>
                        </div>
                    </div>
                    <div className="col-sm-12 col-md-12 col-lg-4">
                        <div className="card shadow bg-success-subtle p-2">
                            <div className="d-flex align-items-center">
                                <InfoOutlineIcon className='me-2' />
                                <p className='mb-0'>Marginal</p>
                            </div>
                            <h3 className='mb-0'>
                                {delticom?.marginal}
                            </h3>
                        </div>
                    </div>
                </div>

            </div>
            <div className="col-sm-12 col-md-12 col-lg-4">
                {delticom?.filled ? (
                    <div className="card p-3 mb-2 bg-success-subtle">
                        <div className="d-flex align-items-center justify-content-center text-center">
                            <CheckOutlineIcon className="me-2 fs-2" />
                            <h4 className="mb-0">Ordern är hanterad.</h4>
                        </div>
                    </div>
                ) : null
                }
                <Link to={`/admin/order/contact?id=${delticom.id}`} className="text-decoration-none">
                    {(contact?.postNr == null || contact?.postAddress == null || contact?.address == null || contact?.country == null || contact?.countryCode == null || contact?.customerName == null) ? (
                        <div className="card p-3 mb-2 bg-danger-subtle shadow-lg border border-dark">
                            <div className="d-flex align-items-center justify-content-center text-center mb-2">
                                <RemindOutlineIcon className="me-2 fs-2" />
                                <h4 className="mb-0">Ordern saknar uppgifter!</h4>
                            </div>
                            <p className='text-center mb-0'>Klicka här för att rätta orderns kontaktuppgifter.</p>
                        </div>
                    ) : null
                    }
                </Link>
                {delticom?.statusCode !== 0 && (
                    <div className="card p-2 mb-2">
                        <div className="d-flex align-items-center">
                            <InfoOutlineIcon className='me-2' />
                            <p className='mb-0'>Pacsoft resultat</p>
                        </div>
                        <h3 className='mb-0'>
                            201
                        </h3>
                    </div>
                )}
                

                <div className="card p-3 mb-2">
                    <div className="d-flex">
                        <h4>Kund</h4>
                        <Link className='ms-auto' to={`/admin/order/contact?id=${delticom.id}`}>
                            <EditIcon className='fs-2' />
                        </Link>
                    </div>
                    <p className='mb-0 mt-0'>{contact?.customerName}</p>
                    <p className='mb-0 mt-0'>{contact?.customerCmpName}</p>
                    <hr />

                    <h4>Orderuppgifter</h4>
                    <p className='mb-0 mt-0'>{delticom?.delticomOrderId}</p>
                    <p className='mb-0 mt-0'>{delticom?.merchant}</p>
                    <p className='mb-0 mt-0'>{delticom?.orderNr}</p>
                    <hr />

                    <h4>Leveransadress</h4>
                    <p className='mb-0 mt-0'>{contact?.address}</p>
                    <p className='mb-0 mt-0'>{contact?.postNr} {contact?.postAddress}</p>
                    <p className='mb-0 mt-0'>{contact?.country}</p>
                    <hr />

                    <h4>Telefon</h4>
                    <p className='mb-0 mt-0'>{contact?.telephone}</p>
                    <hr />

                    <h4>Kolinummer</h4>
                    {parcelNrs?.length > 0 ? (
                    parcelNrs.map((nr, index) => (
                        <a 
                        key={index} 
                        href={`https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=${nr}`} 
                        className='mb-0 mt-0'
                        target="_blank"  // Opens in a new tab
                        rel="noopener noreferrer" // Security best practice for external links
                        >
                        {nr}
                        </a>
                    ))
                    ) : (
                    <p className='text-muted'>Inga kolinummer tillgängliga</p>
                    )}

                </div>

            </div>
        </div>

        </div>
    </>
  );
}

export default DelticomOrder;
